/**
 * Admin-set replacements for interface strings (SCRUM-43), consulted by `t()`.
 *
 * Same shape as the API's mail overrides and for the same reason: `t()` is synchronous and called
 * from every render path, so the overrides are fetched once at boot and read from a module-level
 * map. An empty map is not a degraded mode — it is exactly the behaviour before this existed.
 *
 * ONE LOCALE AT A TIME. Only the locale the page is showing is fetched: the interface dictionary is
 * ~780 keys, and pulling six languages to display one would be six times the payload for nothing.
 * Switching locale reloads the page (see `setLocale`), which re-runs the fetch.
 *
 * A last-good copy is kept in `localStorage`, and that is not a performance trick: the fetch is one
 * network round trip in front of the first paint, so without a cached copy every reload would show
 * the shipped English wording for a moment and then swap it. Serving the cached copy first and
 * refreshing it after makes the swap invisible in the normal case.
 */
const CACHE_PREFIX = "scb-ui-overrides:";

let active: Record<string, string> = {};

export function uiOverride(key: string): string | undefined {
  return active[key];
}

/** Replaces the live map and remembers it for the next boot in this browser. */
export function setUiOverrides(locale: string, map: Record<string, string>): void {
  active = map;
  try {
    localStorage.setItem(CACHE_PREFIX + locale, JSON.stringify(map));
  } catch {
    // A full or disabled localStorage must not break the interface — the map is already live.
  }
}

/** The last copy seen in this browser for `locale`, or an empty map. */
export function cachedUiOverrides(locale: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + locale);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    // Values that are not strings would end up rendered as "[object Object]" in the interface;
    // dropping them is quieter and safer than trusting whatever the cache held.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, v]) => typeof v === "string")
    ) as Record<string, string>;
  } catch {
    return {};
  }
}

/**
 * Placeholders the shipped English string uses. The editor refuses a replacement that invents a new
 * one — `{count}` mistyped as `{cnt}` renders literally in the interface — while dropping one stays
 * allowed, exactly as for mail copy.
 */
export function placeholdersOf(source: string): string[] {
  return [...new Set([...source.matchAll(/\{(\w+)\}/g)].map((match) => match[1] as string))];
}

export function invalidPlaceholders(source: string, value: string): string[] {
  const allowed = new Set(placeholdersOf(source));
  return [...new Set(placeholdersOf(value).filter((name) => !allowed.has(name)))];
}
